import { Component, OnInit, OnChanges, SimpleChanges, inject, signal, Output, EventEmitter, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TaskService, isLikelyUuidFileBase, sanitizeDownloadBasename } from '../../services/task.service';

@Component({
  selector: 'app-file-transfer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './file-transfer.component.html',
  styleUrl: './file-transfer.component.scss',
})
export class FileTransferComponent implements OnInit, OnChanges {
  private taskService = inject(TaskService);

  /** アップロード先パス（親フォームからバインド） */
  @Input() initialFileId = '';
  /** trueのとき：パス入力欄を非表示にし、ボタンだけのコンパクト表示にする */
  @Input() compact = false;
  @Output() uploadSuccessEvent = new EventEmitter<void>();

  fileSystemId = signal<string>('');
  selectedFile = signal<File | null>(null);

  isUploading = signal(false);
  isDownloading = signal(false);
  uploadSuccess = signal(false);
  error = signal<string | null>(null);

  ngOnInit(): void {
    this.fileSystemId.set(this.initialFileId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialFileId']) {
      this.fileSystemId.set(changes['initialFileId'].currentValue || '');
    }
  }

  /**
   * GET/POST に渡すパス。コンパクト時は親の initialFileId（フォームの値）を優先する。
   * 子の fileSystemId だけが ngOnChanges より古い GUID のまま残るとダウンロード名が GUID になるため。
   */
  protected pathForApi(): string {
    const fromParent = (this.initialFileId ?? '').trim();
    return fromParent || this.fileSystemId().trim();
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.selectedFile.set(file);
      this.uploadSuccess.set(false);
      this.error.set(null);
    }
  }

  onSubmit(): void {
    const path = this.pathForApi();
    if (!this.selectedFile() || !path) {
      this.error.set('ファイルとアップロード先パスが必要です。');
      return;
    }

    this.fileSystemId.set(path);

    this.isUploading.set(true);
    this.error.set(null);
    this.uploadSuccess.set(false);

    this.taskService.uploadFileContent(path, this.selectedFile()!).subscribe({
      next: () => {
        this.isUploading.set(false);
        this.uploadSuccess.set(true);
        this.selectedFile.set(null);

        const fileInput = document.getElementById('fileInput') as HTMLInputElement;
        if (fileInput) { fileInput.value = ''; }

        this.uploadSuccessEvent.emit();
      },
      error: (err) => {
        this.isUploading.set(false);
        this.error.set('アップロードに失敗しました。');
        console.error('Upload Error:', err);
      },
    });
  }

  onDownload(): void {
    const path = this.pathForApi();
    if (!path) {
      this.error.set('ダウンロード先パスが必要です。');
      return;
    }

    this.fileSystemId.set(path);

    this.isDownloading.set(true);
    this.error.set(null);

    this.taskService.downloadFile(path).subscribe({
      next: ({ blob, suggestedFileName }) => {
        this.isDownloading.set(false);

        // 画面上のパス（親フォーム）からファイル名を決める。末尾が UUID のときだけサービス推奨名に任せる
        const segment = path.split(/[/\\]/).pop()?.trim() || '';
        const baseFromPath = segment ? sanitizeDownloadBasename(segment) : '';
        const fileName =
          baseFromPath && !isLikelyUuidFileBase(baseFromPath)
            ? baseFromPath
            : suggestedFileName || 'downloaded_file';

        // ブラウザでダウンロードを実行
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      },
      error: (err) => {
        this.isDownloading.set(false);
        this.error.set('ダウンロードに失敗しました。ファイルが存在するか確認してください。');
        console.error('Download Error:', err);
      },
    });
  }
}
